import { jsx } from 'react/jsx-runtime';
import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import { forwardRef, useMemo, useImperativeHandle, useEffect } from 'react';
import deletionRegistry from '../utils/DeletionRegistry.js';
import useGetterRef from '../utils/useGetterRef.js';
import useUnmount from '../utils/useUnmount.js';
import { useRepresentationContext, useDownstreamContext, DatasetContext } from './contexts.js';

const DefaultProps = {
    port: 0,
    dimensions: [1, 1, 1],
    spacing: [1, 1, 1],
    origin: [0, 0, 0],
    direction: [1, 0, 0, 0, 1, 0, 0, 0, 1],
};
var ImageData = forwardRef(function PolyData(props, fwdRef) {
    const { port = DefaultProps.port, dimensions = DefaultProps.dimensions, spacing = DefaultProps.spacing, origin = DefaultProps.origin, direction = DefaultProps.direction, } = props;
    const [imRef, getImageData] = useGetterRef(() => {
        const im = vtkImageData.newInstance();
        deletionRegistry.register(im, () => im.delete());
        return im;
    });
    const representation = useRepresentationContext();
    const downstream = useDownstreamContext();
    // dataset API
    const dataset = useMemo(() => ({
        getDataSet: getImageData,
        modified: () => {
            const im = getImageData();
            im.modified();
            downstream.setInputData(im, port);
            // Let the representation know that we have data
            if (im.getPointData().getScalars().getData()) {
                representation.dataAvailable();
            }
        },
    }), [port, representation, downstream, getImageData]);
    useImperativeHandle(fwdRef, () => dataset);
    // --- update image data model --- //
    useEffect(() => {
        const imageData = getImageData();
        if (imageData.set({ dimensions, spacing, origin, direction })) {
            dataset.modified();
        }
    }, [dimensions, spacing, origin, direction, dataset, getImageData]);
    useUnmount(() => {
        if (imRef.current) {
            representation.dataAvailable(false);
            deletionRegistry.markForDeletion(imRef.current);
            imRef.current = null;
        }
    });
    return (jsx(DatasetContext.Provider, Object.assign({ value: dataset }, { children: props.children })));
});

export { ImageData as default };
